/*
 * The store around the reducer: one Zustand store holding the `Model`, and the bookkeeping the
 * pure `applyEvent` cannot carry — the events held while a conversation loads, the fetch a gap
 * asks for, the retry bound, and the tap through which the platform layer hears about events
 * that change nothing in the model (a notification hint).
 *
 * `applyEvent` decides; this file acts. A "buffered" result parks the event per conversation
 * (≤ 500, and for a conversation the model never learns about no longer than the TTL) and replays
 * it once the conversation settles; a `{resync: conv}` result — and every slice that a reducer put
 * into `resyncPending` — starts one tail fetch through the handler the chat module registers,
 * `resyncPending` being set before the request leaves (REUSE C5); three gaps in a row on one
 * conversation give up and cold-boot (protocol.md §2.8).
 *
 * The handler ends the fetch through `settleConversation`, and only through it: that is where the
 * fetch mark, the held events and the retry count are cleared, and a module that wrote the page
 * into the model on its own would leave the conversation marked as fetching for the life of the tab.
 */

import { useStore as useZustandStore } from "zustand"
import { createStore, type StoreApi } from "zustand/vanilla"
import type { WireEvent } from "../wire/index.ts"
import { applyEvent, withConversation, type ReducerLookup } from "./applyEvent.ts"
import { emptyModel, type Applied, type ConversationId, type Model } from "./model.ts"

/** Events held per conversation while its tail is fetched; above this the oldest is dropped. */
export const MAX_BUFFERED_PER_CONVERSATION = 500

/**
 * Conversations that may hold events at once. A warm sync names at most 200 conversations and the
 * server answers a whole-scope resync above that (`core/net/bootstrap.ts`), so no honest catch-up
 * needs more buckets than this; the ceiling is what keeps events for conversations this account
 * never learns about from growing the map for the life of the tab. Over it, the bucket that has
 * waited longest goes — the same rule the per-conversation cap follows, one level up. Unlike the
 * TTL below, the ceiling spares nothing: the oldest bucket goes even if the model holds that
 * conversation and its page is still on its way, which needs 200 conversations holding events at
 * once — more than a catch-up of this instance reaches.
 */
export const MAX_BUFFERED_CONVERSATIONS = 200

/**
 * How long events are held for a conversation the model does not hold at all. The membership event
 * that would introduce it travels in the same catch-up or not at all (protocol.md §2.8), so a
 * bucket older than this waits for something that is not coming, and `drainSettled` — which skips a
 * conversation with no slice — would never look at it again. A conversation the model *does* hold
 * is never expired here: its events are drained when its fetch settles, however long that takes.
 *
 * The sweep runs when the next event is held, not on a timer: the last stale bucket of a quiet tab
 * outstays the TTL until something else is buffered. It is bounded by the ceiling above and read by
 * nobody, so the TTL is a bound on the map, not a promise about one bucket.
 */
export const BUFFER_TTL_MS = 60_000

/** Consecutive gap refetches of one conversation before the client gives up and cold-boots. */
export const MAX_CONVERSATION_RESYNCS = 3

export interface ResyncHandlers {
	/**
	 * Refetches one conversation's tail and ends by calling `settleConversation`. Registered by the
	 * chat module, which owns the history route; core never names a module's URL. Registering
	 * `undefined` withdraws it, which is what a test that tears its wiring down needs.
	 */
	conversation?: ((conv: ConversationId) => Promise<void> | void) | undefined
	/** Drops the model and bootstraps again; registered by the boot. */
	all?: (() => void) | undefined
}

export type EventListener = (event: WireEvent, result: Applied) => void

/** One conversation's held events and when the last of them arrived, for the bucket's own age. */
interface Buffer {
	events: WireEvent[]
	at: number
}

export class ClientStore {
	private reducerFor: ReducerLookup | undefined
	private handlers: ResyncHandlers = {}
	/** In least-recently-held order: the first entry is the bucket that has waited longest. */
	private readonly buffers = new Map<ConversationId, Buffer>()
	private readonly inFlight = new Set<ConversationId>()
	/**
	 * The conversations whose slice stands in `resyncPending`, so that starting the fetches they ask
	 * for costs the length of this set and not one walk of the conversation map per event. Every
	 * writer of that status is a verdict of `applyEvent` — the gap rule and `rt.resync` — or
	 * `requestResync`, and both refill this set from the model, so a slice cannot be marked without
	 * the store seeing it.
	 */
	private readonly resyncPending = new Set<ConversationId>()
	private readonly resyncTries = new Map<ConversationId, number>()
	private readonly listeners = new Set<EventListener>()
	readonly api: StoreApi<Model>
	private readonly now: () => number

	constructor(api: StoreApi<Model>, now: () => number = Date.now) {
		this.api = api
		this.now = now
	}

	get model(): Model {
		return this.api.getState()
	}

	/** Wires the module registry's lookup in; called once by the boot, after the modules exist. */
	setReducerLookup(lookup: ReducerLookup | undefined): void {
		this.reducerFor = lookup
	}

	/** Each owner registers its own handler — the boot the cold boot, the chat module the fetch. */
	setResyncHandlers(handlers: ResyncHandlers): void {
		this.handlers = { ...this.handlers, ...handlers }
	}

	/** Hears every event after it was applied — or buffered, dropped, refused — with the verdict. */
	onEvent(listener: EventListener): () => void {
		this.listeners.add(listener)
		return () => {
			this.listeners.delete(listener)
		}
	}

	/** Replaces the whole model: a warm boot's hydration, a bootstrap payload, a cold boot. */
	replaceModel(next: Model): void {
		this.api.setState(next, true)
		this.buffers.clear()
		this.inFlight.clear()
		this.resyncPending.clear()
		this.resyncTries.clear()
	}

	/** An identity-preserving patch by an owner outside the event flow (the socket, a module). */
	update(patch: (model: Model) => Model): void {
		const model = this.api.getState()
		const next = patch(model)
		if (next !== model) this.api.setState(next, true)
	}

	/** Applies one event and acts on the verdict. Returns what the reducer did. */
	dispatch(event: WireEvent): Applied {
		return this.apply(event, false)
	}

	/**
	 * Ends a conversation's fetch: the module's patch lands (rows, `lastSeq`, status `ready`), then
	 * the events held meanwhile are replayed in arrival order — a hole among them is a gap like any
	 * other and starts the next fetch, under the retry bound.
	 */
	settleConversation(conv: ConversationId, patch: (model: Model) => Model): void {
		this.update(patch)
		this.inFlight.delete(conv)
		this.drain(conv)
		const status = this.api.getState().conversations[conv]?.status
		// The count of consecutive refetches survives a page that failed: a conversation whose fetch
		// keeps being refused must still reach the bound and cold-boot rather than ask for ever. It
		// is a hole among the replayed events, or a page that landed, that settles the count.
		if (status !== "resyncPending" && status !== "error") this.resyncTries.delete(conv)
		this.startPendingResyncs()
	}

	/** Marks a conversation for refetch and starts the fetch — the order that makes it sound. */
	requestResync(conv: ConversationId): void {
		this.update((model) => {
			const slice = model.conversations[conv]
			if (!slice || slice.status === "resyncPending") return model
			return withConversation(model, conv, { ...slice, status: "resyncPending" })
		})
		if (this.api.getState().conversations[conv]?.status === "resyncPending") {
			this.resyncPending.add(conv)
		}
		this.startPendingResyncs()
	}

	private apply(event: WireEvent, replay: boolean): Applied {
		const model = this.api.getState()
		const { next, result } = applyEvent(model, event, this.reducerFor, { replay })
		if (next !== model) this.api.setState(next, true)
		if (result === "buffered") this.hold(event)
		for (const listener of this.listeners) listener(event, result)
		if (typeof result === "object" && result.resync === "all") {
			this.handlers.all?.()
			return result
		}
		// The one verdict that marks slices. The gap rule marks the conversation it names, and an
		// `rt.resync{convs}` marks every conversation of its list while naming only the first
		// (`applyEvent.reduceResync`), so the answer is read off the model rather than off the
		// verdict — once per gap, not once per event.
		if (typeof result === "object") this.collectPending()
		this.startPendingResyncs()
		if (result === "applied" && !replay) this.drainSettled()
		return result
	}

	private hold(event: WireEvent): void {
		const conv = event.conv
		if (conv === undefined) return
		const now = this.now()
		this.expireBuffers(now)
		const events = this.buffers.get(conv)?.events ?? []
		if (events.length >= MAX_BUFFERED_PER_CONVERSATION) events.shift()
		events.push(event)
		// Re-inserted rather than updated in place: a `Map` keeps insertion order, so the bucket
		// dropped over the ceiling is then the one that has gone longest without an event.
		this.buffers.delete(conv)
		this.buffers.set(conv, { events, at: now })
		while (this.buffers.size > MAX_BUFFERED_CONVERSATIONS) {
			const oldest = this.buffers.keys().next().value
			if (oldest === undefined) break
			this.buffers.delete(oldest)
		}
	}

	/** Forgets what is held for a conversation the model has not learned about within the TTL. */
	private expireBuffers(now: number): void {
		const { conversations } = this.api.getState()
		for (const [conv, held] of this.buffers) {
			if (now - held.at < BUFFER_TTL_MS || conversations[conv] !== undefined) continue
			this.buffers.delete(conv)
		}
	}

	/** Replays the events held for conversations that exist and have no fetch in flight. */
	private drainSettled(): void {
		for (const conv of [...this.buffers.keys()]) {
			const status = this.api.getState().conversations[conv]?.status
			if (status === undefined || status === "loading" || status === "resyncPending") continue
			this.drain(conv)
		}
	}

	private drain(conv: ConversationId): void {
		const held = this.buffers.get(conv)
		if (!held) return
		this.buffers.delete(conv)
		for (const event of held.events) this.apply(event, true)
	}

	/** Reads the slices standing in `resyncPending` off the model, after a verdict that marks one. */
	private collectPending(): void {
		for (const [key, slice] of Object.entries(this.api.getState().conversations)) {
			if (slice.status === "resyncPending") this.resyncPending.add(Number(key))
		}
	}

	/** Every conversation marked `resyncPending` without a fetch in flight gets one, once. */
	private startPendingResyncs(): void {
		for (const conv of [...this.resyncPending]) {
			if (this.api.getState().conversations[conv]?.status !== "resyncPending") {
				this.resyncPending.delete(conv)
				continue
			}
			if (this.inFlight.has(conv)) continue
			this.startResync(conv)
		}
	}

	private startResync(conv: ConversationId): void {
		const tries = (this.resyncTries.get(conv) ?? 0) + 1
		this.resyncTries.set(conv, tries)
		if (tries > MAX_CONVERSATION_RESYNCS) {
			this.resyncTries.delete(conv)
			this.handlers.all?.()
			return
		}
		const fetch = this.handlers.conversation
		if (!fetch) return
		this.inFlight.add(conv)
		Promise.resolve()
			.then(() => fetch(conv))
			.catch(() => {
				// The fetch failed; the conversation shows its retry and the next event tries again.
				this.inFlight.delete(conv)
				this.update((model) => {
					const slice = model.conversations[conv]
					return slice ? withConversation(model, conv, { ...slice, status: "error" }) : model
				})
			})
	}
}

/**
 * A fresh store over an empty model; the application has one, every test makes its own. `now` is
 * the clock the held events are aged by, so a test can walk it forward instead of waiting.
 */
export function createClientStore(
	initial: Model = emptyModel(),
	now: () => number = Date.now,
): ClientStore {
	return new ClientStore(
		createStore<Model>(() => initial),
		now,
	)
}

export const client = createClientStore()

/** Reads one slice of the application's model from a component; re-renders when it changes. */
export function useModel<U>(selector: (model: Model) => U): U {
	return useZustandStore(client.api, selector)
}
